import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, MapPin, Shield, Heart, FileCheck, Phone, Linkedin, Instagram } from 'lucide-react';
import { BBBSeal } from '../BBBSeal';

export const Footer = () => {
  const { t } = useTranslation();

  const footerLinks = {
    coverage: [
      { name: t('nav.communityOrganizations'), path: '/community-organizations' },
      { name: t('nav.healthPlans'), path: '/health-plans' },
      { name: t('nav.dentalVision'), path: '/dental-vision' },
      { name: t('nav.medicare'), path: '/medicare' },
      { name: t('nav.smallBusiness'), path: '/small-business' },
    ],
    enrollment: [
      { name: t('nav.openEnrollment'), path: '/open-enrollment' },
      { name: t('nav.specialEnrollment'), path: '/special-enrollment' },
      { name: t('nav.medicarePlans'), path: '/medicare' },
      { name: t('footer.contactUs'), path: '/contact' },
    ],
    company: [
      { name: t('nav.aboutUs'), path: '/about' },
      { name: t('footer.contactUs'), path: '/contact' },
      { name: t('nav.workWithUs'), path: '/work-with-us' },
    ],
  };

  return (
    <footer className="bg-[#020617] text-white border-t border-slate-800" data-testid="footer">
      {/* Main Footer */}
      <div className="container-custom py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <p className="text-slate-400 text-sm leading-relaxed mb-6">
              {t('footer.description')}
            </p>
            <div className="flex items-center gap-2 text-slate-400">
              <Mail className="h-4 w-4 text-sky-400" />
              <a 
                href="mailto:info@calyvrainsurance.com" 
                className="text-sm hover:text-white transition-colors"
                data-testid="footer-email"
              >
                info@calyvrainsurance.com
              </a>
            </div>
            <div className="flex items-center gap-2 text-slate-400 mt-3">
              <Phone className="h-4 w-4 text-sky-400" />
              <a 
                href="tel:+13054323557" 
                className="text-sm hover:text-white transition-colors font-semibold"
                data-testid="footer-phone"
              >
                (305) 432-3557
              </a>
            </div>
            <div className="flex items-center gap-3 mt-5">
              <a
                href="https://www.linkedin.com/company/calyvra-insurance"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Calyvra Insurance on LinkedIn"
                className="h-9 w-9 flex items-center justify-center rounded-full bg-slate-800 text-slate-300 hover:bg-sky-500 hover:text-white transition-colors"
                data-testid="footer-linkedin"
              >
                <Linkedin className="h-4 w-4" />
              </a>
              <a
                href="https://www.instagram.com/calyvrainsurance/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Calyvra Insurance on Instagram"
                className="h-9 w-9 flex items-center justify-center rounded-full bg-slate-800 text-slate-300 hover:bg-sky-500 hover:text-white transition-colors"
                data-testid="footer-instagram"
              >
                <Instagram className="h-4 w-4" />
              </a>
            </div>
          </div>

          {/* Coverage Links */}
          <div>
            <h4 className="font-semibold text-white mb-4 flex items-center gap-2">
              <Shield className="h-4 w-4 text-sky-400" />
              {t('footer.coverage')}
            </h4>
            <ul className="space-y-3">
              {footerLinks.coverage.map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path}
                    className="text-slate-400 hover:text-white text-sm transition-colors"
                    data-testid={`footer-link-${link.path.slice(1)}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Enrollment Links */}
          <div>
            <h4 className="font-semibold text-white mb-4 flex items-center gap-2">
              <FileCheck className="h-4 w-4 text-sky-400" />
              {t('footer.enrollment')}
            </h4>
            <ul className="space-y-3">
              {footerLinks.enrollment.map((link) => (
                <li key={`enroll-${link.path}`}>
                  <Link 
                    to={link.path}
                    className="text-slate-400 hover:text-white text-sm transition-colors"
                    data-testid={`footer-enroll-link-${link.path.slice(1)}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="font-semibold text-white mb-4 flex items-center gap-2">
              <Heart className="h-4 w-4 text-sky-400" />
              {t('footer.company')}
            </h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={`company-${link.path}`}>
                  <Link 
                    to={link.path}
                    className="text-slate-400 hover:text-white text-sm transition-colors"
                    data-testid={`footer-company-link-${link.path.slice(1)}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/privacy-policy"
                  className="text-slate-500 hover:text-slate-300 text-xs transition-colors"
                  data-testid="footer-privacy-link"
                >
                  {t('footer.privacyPolicy')}
                </Link>
              </li>
              <li>
                <Link
                  to="/sms-terms"
                  className="text-slate-500 hover:text-slate-300 text-xs transition-colors"
                  data-testid="footer-sms-terms-link"
                >
                  {t('footer.smsTerms')}
                </Link>
              </li>
            </ul>
            
            {/* Florida Badge */}
            <div className="mt-6 inline-flex items-center gap-2 px-3 py-2 bg-slate-800 rounded-lg">
              <MapPin className="h-4 w-4 text-sky-400" />
              <span className="text-xs text-slate-300">{t('footer.licensedInFlorida')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-800">
        <div className="container-custom py-6">
          <div className="flex flex-col md:flex-row md:items-start gap-6 md:gap-10">
            <div className="flex justify-center md:justify-start flex-shrink-0" data-testid="footer-bbb-seal">
              <BBBSeal width={150} />
            </div>
            <div className="flex-1 space-y-2">
              <p className="text-slate-400 text-xs leading-relaxed text-center md:text-left" data-testid="footer-disclaimer">
                {t('footer.disclaimer')}
              </p>
              <p className="text-slate-400 text-xs leading-relaxed text-center md:text-left" data-testid="footer-compliance">
                {t('footer.complianceDisclaimer')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
